<script setup>
import Icon from '@/Components/Icon.vue';
import ImgMedia from '@/Components/Images/ImgMedia.vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';
import envelope from '@i/envelope.svg';
import imageIcon from '@i/image.svg';
import phoneIcon from '@i/phone.svg';
const props = defineProps({
    modelValue: Boolean,
    member: Object,
});

const emit = defineEmits(['update:modelValue']);

function closeModal() {
    emit('update:modelValue', false);
}
</script>

<template>
    <TransitionRoot as="template" :show="modelValue">
        <Dialog as="div" class="relative z-50" @close="closeModal">
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </TransitionChild>

            <div class="fixed inset-0 z-10 overflow-y-auto">
                <div class="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            class="relative px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:w-full sm:max-w-[900px] sm:p-6"
                        >
                            <div class="absolute top-0 right-0 z-10 pt-4 pr-4">
                                <button
                                    type="button"
                                    class="text-gray-400 bg-white rounded-md hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    @click="closeModal"
                                >
                                    <span class="sr-only">Close</span>
                                    <XMarkIcon class="w-6 h-6" aria-hidden="true" />
                                </button>
                            </div>
                            <div>
                                <div class="relative overflow-hidden">
                                    <div class="">
                                        <h2 class="" v-html="member.name"></h2>
                                        <div
                                            v-if="member.job_position"
                                            v-html="member.job_position"
                                            class="max-w-full mb-8 -mt-4 prose md:-mt-12 text-bodyColor marker:text-primary"
                                        ></div>
                                    </div>
                                    <div class="flex flex-col-reverse w-full md:flex-row">
                                        <div class="flex-1" v-html="member.text"></div>
                                        <div class="md:w-[40%] mb-8 md:mx-8">
                                            <div class="relative aspect-[1/1]" v-if="member.image">
                                                <ImgMedia
                                                    :media="member.image"
                                                    classes="absolute top-0 left-0 right-0 object-cover w-full h-full rounded-xl transition-transform duration-300"
                                                />
                                            </div>
                                            <div
                                                class="flex items-center justify-center w-full min-h-[155px] sm:min-h-[250px] md:min-h-[400px] 2xl:min-h-[300px] bg-gray-light rounded-tl-xl rounded-tr-xl"
                                                v-else
                                            >
                                                <img
                                                    :src="imageIcon"
                                                    class="w-4 h-4 md:w-10 md:h-10 opacity-10"
                                                    alt="icon"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <h2 class="my-8">"{{ member.quote }}"</h2>
                                    <div class="md:p-4">
                                        <div
                                            class="flex flex-col justify-end gap-4 md:gap-8 md:items-center md:flex-row"
                                        >
                                            <div v-if="member.phone">
                                                <div class="flex items-center gap-2">
                                                    <Icon
                                                        :icon="phoneIcon"
                                                        :iconDutch="phoneIcon"
                                                        alt="phone icon"
                                                        class="w-4 h-auto shrink-0"
                                                    />
                                                    <a :href="`tel:${member.phone}`" class="hover:text-primary">{{
                                                        member.phone
                                                    }}</a>
                                                </div>
                                            </div>

                                            <div v-if="member.email">
                                                <div class="flex items-center gap-2">
                                                    <Icon
                                                        :icon="envelope"
                                                        :iconDutch="envelope"
                                                        alt="envelope icon"
                                                        class="w-5 h-auto shrink-0"
                                                    />
                                                    <a :href="`mailto:${member.email}`" class="hover:text-primary">{{
                                                        member.email
                                                    }}</a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
